// src/services/authService.ts - verbesserte Token-Aktualisierung
import axios from 'axios';
import { API_CONFIG } from '../config/api';

let cachedArtistInfo: LoginStatus['artist'] | null = null;
let lastArtistFetch = 0;
const CACHE_TTL = 60000; // 1 minute cache

// Types für Spotify Auth
export interface SpotifyAuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}
export interface LoginStatus {
  isLoggedIn: boolean;
  user?: {
    id: string;
    spotifyId: string;
    displayName: string;
  };
  artist?: {
    id: string;
    spotifyId: string;
    name: string;
    linkId: string;
  } | null;
  error?: string;
}
export interface SpotifyUserProfile {
  id: string;
  displayName: string;
  email: string;
  images: Array<{ url: string }>;
}

export const authEvents = {
  emitChange: () => {
    console.log("Auth-Status hat sich geändert, Event wird ausgelöst");
    window.dispatchEvent(new Event('auth-change'));
  }
};

export interface SpotifyArtistProfile {
  id: string;
  name: string;
  isVerified: boolean;
}

// Auth-State speichern
let authState: {
  tokens: SpotifyAuthTokens | null;
  userProfile: SpotifyUserProfile | null;
  artistProfile: SpotifyArtistProfile | null;
  isConnected: boolean;
  lastAuthCheck: number; // Zeitstempel der letzten Auth-Prüfung
  refreshing: boolean; // Flag, um parallele Token-Aktualisierungen zu vermeiden
} = {
  tokens: null,
  userProfile: null,
  artistProfile: null,
  isConnected: false,
  lastAuthCheck: 0,
  refreshing: false
};

// Erweiterte Rückgabe der Authentifizierungsprüfung
export interface AuthStatus {
  isAuthenticated: boolean;
  needsRenewal: boolean;
  expiryTime?: Date;
  message?: string;
}

// Prüfen des Authentifizierungsstatus mit detaillierten Informationen
export const checkAuthStatus = (): AuthStatus => {
  try {
    // Grundlegende Prüfung, ob Token vorhanden ist
    if (!authState.isConnected || !authState.tokens?.accessToken) {
      return {
        isAuthenticated: false,
        needsRenewal: true,
        message: "Nicht angemeldet. Bitte melde dich an, um fortzufahren."
      };
    }

    const tokenExpiry = localStorage.getItem('spotify_token_expiry');
    
    // Wenn kein Ablaufzeitpunkt vorhanden ist
    if (!tokenExpiry) {
      console.log("Kein Token-Ablaufzeitpunkt vorhanden");
      return {
        isAuthenticated: false,
        needsRenewal: true,
        message: "Sitzung abgelaufen. Bitte melde dich erneut an."
      };
    }

    const expiryDate = new Date(tokenExpiry);
    const now = new Date();
    
    // Token ist noch gültig
    if (expiryDate > now) {
      // Wenn Token bald abläuft (in weniger als 5 Minuten), im Hintergrund aktualisieren
      if (expiryDate.getTime() - now.getTime() < 5 * 60 * 1000) {
        console.log("Token läuft bald ab, wird im Hintergrund aktualisiert");
        if (!authState.refreshing) {
          setTimeout(() => refreshAccessToken(), 0);
        }
        return {
          isAuthenticated: true,
          needsRenewal: false,
          expiryTime: expiryDate,
          message: "Deine Sitzung läuft bald ab, wird automatisch verlängert."
        };
      }
      
      // Token ist noch länger gültig
      return {
        isAuthenticated: true,
        needsRenewal: false,
        expiryTime: expiryDate
      };
    }
    
    // Token ist abgelaufen, aber wir versuchen es zu aktualisieren
    console.log("Token ist abgelaufen, versuche zu aktualisieren");
    
    // Starten wir einen Aktualisierungsprozess im Hintergrund, wenn keiner läuft
    if (!authState.refreshing) {
      setTimeout(() => refreshAccessToken(), 0);
    }
    
    // Prüfen, wie lange das Token schon abgelaufen ist
    const minutesSinceExpiry = (now.getTime() - expiryDate.getTime()) / (1000 * 60);
    
    // Wenn das Token weniger als 60 Minuten abgelaufen ist, geben wir noch eine Chance zur Aktualisierung
    if (minutesSinceExpiry < 60) {
      return {
        isAuthenticated: true, // Wir betrachten den Nutzer vorübergehend als authentifiziert
        needsRenewal: false,
        expiryTime: expiryDate,
        message: "Deine Sitzung wird aktualisiert..."
      };
    } else {
      // Token ist zu lange abgelaufen, erfordert Neuanmeldung
      return {
        isAuthenticated: false,
        needsRenewal: true,
        expiryTime: expiryDate,
        message: "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an."
      };
    }
  } catch (error) {
    console.error("Fehler bei Authentifizierungsprüfung:", error);
    return {
      isAuthenticated: false,
      needsRenewal: true,
      message: "Fehler bei der Überprüfung deiner Anmeldung. Bitte melde dich erneut an."
    };
  }
};
// Add this method to authService.ts
/**
 * Fetch detailed login status including linked artist information
 */
export const getLoginStatus = async (): Promise<LoginStatus> => {
  try {
    const response = await axios.get(
      `${API_CONFIG.BASE_URL}/api/auth/status`,
      {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching login status:', error);
    
    // Return default status with error message
    return {
      isLoggedIn: false,
      error: 'Failed to fetch login status'
    };
  }
};
// Vereinfachte Methode für Abwärtskompatibilität
export const isAuthenticated = (): boolean => {
  const status = checkAuthStatus();
  return status.isAuthenticated;
};

// Auth aus localStorage beim App-Start initialisieren
export const initAuth = async (): Promise<boolean> => {
  try {
    // First try to get auth from local storage
    const storedAuth = localStorage.getItem('spotify_auth');
    if (storedAuth) {
      authState = JSON.parse(storedAuth);
      
      // Initialize auth state properties if needed
      if (authState.refreshing === undefined) {
        authState.refreshing = false;
      }
      
      if (!authState.lastAuthCheck) {
        authState.lastAuthCheck = 0;
      }
      
      // Verify the token with server
      try {
        const loginStatus = await getLoginStatus();
        
        // If server says we're not logged in but local storage says we are,
        // we need to refresh the token or reset auth state
        if (!loginStatus.isLoggedIn && authState.tokens?.accessToken) {
          console.log("Token verification failed, trying to refresh...");
          const refreshSuccess = await refreshAccessToken();
          
          if (!refreshSuccess) {
            console.log("Token refresh failed, resetting auth state");
            resetAuthState();
            return false;
          }
          
          // After successful refresh, check status again
          const refreshedStatus = await getLoginStatus();
          if (!refreshedStatus.isLoggedIn) {
            console.log("Still not logged in after token refresh");
            resetAuthState();
            return false;
          }
        }
        
        // Update artist info if available
        if (loginStatus.artist) {
          authState.artistProfile = {
            id: loginStatus.artist.id,
            name: loginStatus.artist.name,
            isVerified: true
          };
        }
      } catch (statusError) {
        console.error("Error checking login status:", statusError);
        // Continue with local auth data
      }
      
      // Set isConnected based on token presence
      if (authState.tokens?.accessToken) {
        authState.isConnected = true;
        authState.lastAuthCheck = Date.now();
        
        // Save updated auth state
        saveAuthState();
        
        // Trigger auth change event
        authEvents.emitChange();
        return true;
      }
    }
    
    // If no stored auth, try to get status from server
    try {
      const loginStatus = await getLoginStatus();
      if (loginStatus.isLoggedIn && loginStatus.user) {
        // We're logged in on the server but not locally - fetch a fresh token
        const refreshSuccess = await refreshAccessToken();
        return refreshSuccess;
      }
    } catch (err) {
      console.error("Error checking server login status:", err);
    }
    
    return false;
  } catch (error) {
    console.error('Error initializing auth:', error);
    // Reset auth state on error
    localStorage.removeItem('spotify_auth');
    localStorage.removeItem('spotify_token_expiry');
    resetAuthState();
    return false;
  }
};
/**
 * Get current linked artist information from backend
 * Uses caching to reduce API calls
 */
export const getCurrentArtistInfo = async (): Promise<LoginStatus['artist'] | null> => {
  const now = Date.now();
  
  // Return cached value if it exists and is not expired
  if (cachedArtistInfo !== null && now - lastArtistFetch < CACHE_TTL) {
    return cachedArtistInfo;
  }
  
  try {
    const status = await getLoginStatus();
    
    // Update cache
    cachedArtistInfo = status.artist || null;
    lastArtistFetch = now;
    
    return cachedArtistInfo;
  } catch (error) {
    console.error('Error fetching current artist info:', error);
    return null;
  }
};

/**
 * Invalidate the artist info cache when the user links/unlinks artists
 */
export const invalidateArtistCache = (): void => {
  cachedArtistInfo = null;
  lastArtistFetch = 0;
};



/**
 * Check if user has any linked artist
 */
export const hasLinkedArtist = async (): Promise<boolean> => {
  const artistInfo = await getCurrentArtistInfo();
  return artistInfo !== null;
};
// Hilfsfunktion zum Speichern des Auth-Status im localStorage
const saveAuthState = (): void => {
  try {
    localStorage.setItem('spotify_auth', JSON.stringify(authState));
  } catch (error) {
    console.error('Fehler beim Speichern des Auth-Status:', error);
  }
};

// Auth-State auf Standardwerte zurücksetzen
const resetAuthState = (): void => {
  authState = {
    tokens: null,
    userProfile: null,
    artistProfile: null,
    isConnected: false,
    lastAuthCheck: 0,
    refreshing: false
  };
  
  // Auch localStorage löschen
  localStorage.removeItem('spotify_auth');
  localStorage.removeItem('spotify_token_expiry');
  
  // Auth-Change-Event auslösen
  authEvents.emitChange();
};

// Login-URL mit korrekten Scopes für Spotify for Artists generieren
export const getLoginUrl = (): string => {
  const clientId = API_CONFIG.SPOTIFY.CLIENT_ID;
  const redirectUri = encodeURIComponent(API_CONFIG.SPOTIFY.REDIRECT_URI);
  
  // Benötigte Scopes für Spotify for Artists Zugriff
  const scopes = encodeURIComponent([
    'user-read-private',
    'user-read-email',
    'ugc-image-upload',
    // Weitere Scopes nach Bedarf hinzufügen
  ].join(' '));
  
  return `https://accounts.spotify.com/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&scope=${scopes}`;
};

// Zur Login-Seite weiterleiten
export const redirectToLogin = (returnPath?: string): void => {
  // Rückgabepfad speichern, falls angegeben
  if (returnPath) {
    sessionStorage.setItem('returnAfterLogin', returnPath);
  }
  
  // Weiterleitung zur Login-Seite
  window.location.href = getLoginUrl();
};

// Funktion zum Abrufen des Rückgabepfads nach der Anmeldung
export const getReturnPathAfterLogin = (): string => {
  const returnPath = sessionStorage.getItem('returnAfterLogin');
  // Gespeicherten Pfad löschen
  sessionStorage.removeItem('returnAfterLogin');
  return returnPath || '/add-metrics';
};

// Auth-Callback von Spotify verarbeiten
export const handleAuthCallback = async (code: string): Promise<boolean> => {
  try {
    const response = await axios.post(`${API_CONFIG.BASE_URL}/api/auth/spotify-callback`, { code });
    
    if (response.data && response.data.accessToken) {
      console.log("Auth-Callback erfolgreich, Token und Benutzerinfos werden gespeichert");
      
      // Tokens speichern
      authState.tokens = {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
        expiresIn: response.data.expiresIn,
        tokenType: response.data.tokenType
      };
      
      // Benutzerprofil aus der Antwort speichern
      if (response.data.user) {
        authState.userProfile = {
          id: response.data.user.id,
          displayName: response.data.user.displayName || '',
          email: response.data.user.email || '',
          images: response.data.user.images || []
        };
      }
      
      // Token-Ablaufzeit berechnen, mit 1 Minute Puffer
      const expiryTime = new Date();
      expiryTime.setSeconds(expiryTime.getSeconds() + response.data.expiresIn - 60);
      localStorage.setItem('spotify_token_expiry', expiryTime.toISOString());
      
      // Verbindungsstatus aktualisieren
      authState.isConnected = true;
      authState.lastAuthCheck = Date.now();
      authState.refreshing = false;
      
      // Auth-Status im localStorage speichern
      saveAuthState();
      
      // Auth-Change-Event auslösen
      authEvents.emitChange();
      
      return true;
    }
    return false;
  } catch (error) {
    console.error('Fehler beim Verarbeiten des Auth-Callbacks:', error);
    return false;
  }
};

// Access-Token mit dem Refresh-Token aktualisieren
export const refreshAccessToken = async (): Promise<boolean> => {
  // Wenn bereits eine Aktualisierung läuft, nichts tun
  if (authState.refreshing) {
    console.log("Token-Aktualisierung läuft bereits, überspringe");
    return true;
  }
  
  try {
    authState.refreshing = true;
    
    if (!authState.tokens?.refreshToken) {
      console.log("Kein Refresh-Token vorhanden");
      authState.refreshing = false;
      
      // Auth-Change-Event auslösen, um Benachrichtigungen zu aktualisieren
      authEvents.emitChange();
      return false;
    }
    
    console.log("Starte Token-Aktualisierung...");
    
    // Zuerst versuchen wir die Backend-Methode
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/api/auth/refresh-token`, {
        refreshToken: authState.tokens.refreshToken
      });
      
      if (response.data && response.data.accessToken) {
        // Token-Infos aktualisieren
        authState.tokens = {
          ...authState.tokens,
          accessToken: response.data.accessToken,
          expiresIn: response.data.expiresIn,
        };
        
        // Ablaufzeit aktualisieren, mit 1 Minute Puffer
        const expiryTime = new Date();
        expiryTime.setSeconds(expiryTime.getSeconds() + response.data.expiresIn - 60);
        localStorage.setItem('spotify_token_expiry', expiryTime.toISOString());
        
        // Status aktualisieren
        authState.isConnected = true;
        authState.lastAuthCheck = Date.now();
        authState.refreshing = false;
        
        // Aktualisierter Status speichern
        saveAuthState();
        
        console.log("Token erfolgreich aktualisiert, gültig bis:", expiryTime.toLocaleString());
        
        // Auth-Change-Event auslösen, um Benachrichtigungen zu aktualisieren
        authEvents.emitChange();
        return true;
      }
    } catch (backendError) {
      console.warn("Backend Token-Aktualisierung fehlgeschlagen:", backendError);
      // Wir versuchen es mit der direkten Methode weiter unten
    }
    
    // Wenn das Backend fehlschlägt, versuchen wir die direkte Methode mit dem Spotify-API
    try {
      // Diese URL sollte in deiner Konfiguration definiert sein
      const tokenEndpoint = API_CONFIG.SPOTIFY.TOKEN_ENDPOINT;
      const clientId = API_CONFIG.SPOTIFY.CLIENT_ID;
      
      const formData = new URLSearchParams();
      formData.append('grant_type', 'refresh_token');
      formData.append('refresh_token', authState.tokens.refreshToken);
      formData.append('client_id', clientId);
      
      const response = await axios.post(tokenEndpoint, formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      if (response.data && response.data.access_token) {
        // Token-Infos aktualisieren
        authState.tokens = {
          ...authState.tokens,
          accessToken: response.data.access_token,
          expiresIn: response.data.expires_in,
        };
        
        // Aktualisiere auch das Refresh-Token, falls es in der Antwort enthalten ist
        if (response.data.refresh_token) {
          authState.tokens.refreshToken = response.data.refresh_token;
        }
        
        // Ablaufzeit aktualisieren, mit 1 Minute Puffer
        const expiryTime = new Date();
        expiryTime.setSeconds(expiryTime.getSeconds() + response.data.expires_in - 60);
        localStorage.setItem('spotify_token_expiry', expiryTime.toISOString());
        
        // Status aktualisieren
        authState.isConnected = true;
        authState.lastAuthCheck = Date.now();
        
        // Aktualisierter Status speichern
        saveAuthState();
        
        console.log("Token erfolgreich aktualisiert über direkte Methode, gültig bis:", expiryTime.toLocaleString());
        
        // Versuche auch das Token im Backend zu aktualisieren
        try {
          await axios.post(`${API_CONFIG.BASE_URL}/api/auth/update-token`, {
            accessToken: response.data.access_token
          });
          console.log("Backend-Token wurde ebenfalls aktualisiert");
        } catch (updateError) {
          console.warn("Backend-Token konnte nicht aktualisiert werden:", updateError);
          // Aber Frontend-Auth ist trotzdem OK
        }
        
        authState.refreshing = false;
        
        // Auth-Change-Event auslösen, um Benachrichtigungen zu aktualisieren
        authEvents.emitChange();
        return true;
      }
    } catch (directError) {
      console.error("Direkte Token-Aktualisierung fehlgeschlagen:", directError);
    }
    
    // Wenn alle Methoden fehlschlagen
    authState.refreshing = false;
    
    // Auth-Change-Event auslösen, um Benachrichtigungen zu aktualisieren
    authEvents.emitChange();
    return false;
  } catch (error) {
    console.error('Fehler bei Token-Aktualisierung:', error);
    authState.refreshing = false;
    
    // Auth-Change-Event auslösen, um Benachrichtigungen zu aktualisieren
    authEvents.emitChange();
    return false;
  }
};

// Aktuelles Access-Token abrufen
export const getAccessToken = (): string | null => {
  try {
    // Zuerst prüfen, ob das Token abgelaufen ist
    const tokenExpiry = localStorage.getItem('spotify_token_expiry');
    
    if (tokenExpiry) {
      const expiryDate = new Date(tokenExpiry);
      const now = new Date();
      
      // Wenn das Token abgelaufen ist und keine Aktualisierung läuft, im Hintergrund aktualisieren
      if (expiryDate <= now && !authState.refreshing) {
        console.log("Token ist abgelaufen, starte Aktualisierung im Hintergrund");
        setTimeout(() => refreshAccessToken(), 0);
      }
      // Wenn das Token bald abläuft (< 5 Minuten) und keine Aktualisierung läuft, im Hintergrund aktualisieren
      else if (expiryDate.getTime() - now.getTime() < 5 * 60 * 1000 && !authState.refreshing) {
        console.log("Token läuft bald ab, starte Aktualisierung im Hintergrund");
        setTimeout(() => refreshAccessToken(), 0);
      }
    }
    
    // Wenn authState bereits initialisiert wurde, nutze den dort gespeicherten Token
    if (authState.tokens?.accessToken) {
      return authState.tokens.accessToken;
    }
    
    // Ansonsten versuche, ihn aus dem localStorage zu holen
    const storedAuth = localStorage.getItem('spotify_auth');
    if (storedAuth) {
      const auth = JSON.parse(storedAuth);
      if (auth.tokens?.accessToken) {
        return auth.tokens.accessToken;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Fehler beim Abrufen des Access-Tokens:', error);
    return null;
  }
};

// Benutzerprofil abrufen
export const fetchUserProfile = async (): Promise<SpotifyUserProfile | null> => {
  if (!isAuthenticated()) {
    console.log("Nicht authentifiziert, kann Profil nicht abrufen");
    return null;
  }
  
  try {
    // Direkte Anfrage an Spotify API
    const token = getAccessToken();
    if (!token) {
      console.log("Kein gültiges Token vorhanden");
      return null;
    }
    
    const spotifyResponse = await axios.get('https://api.spotify.com/v1/me', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (spotifyResponse.data && spotifyResponse.data.id) {
      const profile = {
        id: spotifyResponse.data.id,
        displayName: spotifyResponse.data.display_name || '',
        email: spotifyResponse.data.email || '',
        images: spotifyResponse.data.images || []
      };
      
      // Lokalen Status aktualisieren
      authState.userProfile = profile;
      saveAuthState();
      
      return profile;
    }
    
    return null;
  } catch (error) {
    console.error('Fehler beim Abrufen des Benutzerprofils:', error);
    
    // Bei 401 Fehler versuchen wir das Token zu aktualisieren und erneut abzufragen
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      try {
        const refreshSuccess = await refreshAccessToken();
        if (refreshSuccess) {
          // Erneuter Versuch nach Token-Aktualisierung
          return await fetchUserProfile();
        }
      } catch (refreshError) {
        console.error("Fehler bei Token-Aktualisierung nach Profil-Fehler:", refreshError);
      }
    }
    
    return null;
  }
};

// Abmelden und gespeicherte Daten löschen
export const logout = async (): Promise<boolean> => {
  try {
    // Server-Logout-Endpunkt aufrufen
    await axios.post(`${API_CONFIG.BASE_URL}/api/auth/logout`);
  } catch (error) {
    console.error('Fehler beim Server-Logout:', error);
    // Mit lokalem Logout fortfahren, auch wenn Server-Logout fehlschlägt
  } finally {
    // Auth-Status löschen
    resetAuthState();
    return true;
  }
};

// Aktuelle Spotify-Benutzer-ID abrufen
export const getCurrentUserId = (): string | null => {
  return authState.userProfile?.id || null;
};

// Aktuellen Anzeigenamen abrufen
export const getCurrentUserName = (): string | null => {
  return authState.userProfile?.displayName || null;
};

// Aktuelle Spotify-Künstler-ID abrufen
export const getCurrentArtistId = (): string | null => {
  return authState.artistProfile?.id || null;
};

/**
 * Get the currently linked artist name
 */
export const getCurrentArtistName = async (): Promise<string | null> => {
  const artistInfo = await getCurrentArtistInfo();
  return artistInfo?.name || null;
};

// Axios-Interceptor für automatisches Hinzufügen des Tokens zu Anfragen
axios.interceptors.request.use(
  (config) => {
    // Token nur für Spotify-API-Anfragen oder unser Backend hinzufügen
    if ((config.url?.includes('api.spotify.com') || config.url?.includes(API_CONFIG.BASE_URL)) && authState.tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${authState.tokens.accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Axios-Interceptor für automatische Token-Aktualisierung bei 401-Fehlern
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: any) => void;
  config: any;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.config.headers['Authorization'] = `Bearer ${token}`;
      prom.resolve(axios(prom.config));
    }
  });
  
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Wenn Fehler 401 ist und wir nicht bereits versucht haben, das Token zu aktualisieren
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wenn bereits eine Aktualisierung läuft, füge die Anfrage zur Warteschlange hinzu
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest });
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Token aktualisieren
        const success = await refreshAccessToken();
        
        if (success && authState.tokens?.accessToken) {
          // Auth-Header mit neuem Token aktualisieren
          originalRequest.headers.Authorization = `Bearer ${authState.tokens.accessToken}`;
          
          // Warteschlange verarbeiten
          processQueue(null, authState.tokens.accessToken);
          
          isRefreshing = false;
          
          // Ursprüngliche Anfrage wiederholen
          return axios(originalRequest);
        } else {
          // Wenn Aktualisierung fehlschlägt
          processQueue(error, null);
          isRefreshing = false;
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);